import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectProfile } from '../redux/slices/userSlice';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(20, 20, 20, 1) 100%
  ),
  url('https://images.unsplash.com/photo-1489599162589-42fb2bf2eaa2?w=1920&h=1080&fit=crop') center/cover;
  padding: 2rem;
`;

const NetflixLogo = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: #E50914;
  margin-bottom: 3rem;
  font-family: 'Netflix Sans', sans-serif;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  font-weight: 400;
  text-align: center;
  margin-bottom: 2rem;
  color: white;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ProfileGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  justify-content: center;
  max-width: 1000px;
`;

const ProfileCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const ProfileAvatar = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1rem;
  border: 3px solid transparent;
  transition: border-color 0.3s ease;

  ${ProfileCard}:hover & {
    border-color: white;
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProfileName = styled.span`
  font-size: 1.3rem;
  color: #808080;
  transition: color 0.3s ease;

  ${ProfileCard}:hover & {
    color: white;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const AddProfileCard = styled(ProfileCard)`
  opacity: 0.8;
`;

const AddProfileAvatar = styled(ProfileAvatar)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  border: 2px dashed rgba(255, 255, 255, 0.3);
`;

const PlusIcon = styled.div`
  font-size: 4rem;
  color: rgba(255, 255, 255, 0.5);
`;

const ManageProfilesButton = styled.button`
  margin-top: 3rem;
  padding: 0.5rem 1.5rem;
  background: transparent;
  border: 1px solid #808080;
  color: #808080;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: white;
    border-color: white;
  }
`;

const ProfileSelection = () => {
  const dispatch = useDispatch();
  const { profiles } = useSelector(state => state.user);
  const [hoveredProfile, setHoveredProfile] = useState(null);

  const handleProfileSelect = (profile) => {
    dispatch(selectProfile(profile));
  };

  const handleKeyDown = (event, profile) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleProfileSelect(profile);
    }
  };

  return (
    <Container>
      <NetflixLogo>NETFLIX</NetflixLogo>
      <Title>Who's watching?</Title>

      <ProfileGrid>
        {profiles.map((profile, index) => (
          <ProfileCard
            key={profile.id}
            onClick={() => handleProfileSelect(profile)}
            onKeyDown={(e) => handleKeyDown(e, profile)}
            onMouseEnter={() => setHoveredProfile(profile.id)}
            onMouseLeave={() => setHoveredProfile(null)}
            tabIndex={0}
            role="button"
            aria-label={`Select ${profile.name} profile`}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <ProfileAvatar>
              <AvatarImage
                src={profile.avatar}
                alt={`${profile.name} profile`}
                loading="lazy"
              />
            </ProfileAvatar>
            <ProfileName>{profile.name}</ProfileName>
          </ProfileCard>
        ))}

        {profiles.length < 5 && (
          <AddProfileCard
            tabIndex={0}
            role="button"
            aria-label="Add new profile"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: profiles.length * 0.1, duration: 0.5 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <AddProfileAvatar>
              <PlusIcon>+</PlusIcon>
            </AddProfileAvatar>
            <ProfileName>Add Profile</ProfileName>
          </AddProfileCard>
        )}
      </ProfileGrid>

      <ManageProfilesButton
        tabIndex={0}
        aria-label="Manage profiles"
      >
        Manage Profiles
      </ManageProfilesButton>
    </Container>
  );
};

export default ProfileSelection;